/**
 * AIChatConversationProvider — owns the Conversation slice (the mode-agnostic chat engine +
 * `mode`) via its own useReducer.
 *
 * The INNERMOST of the four nested slice providers, directly wrapping the effects-only
 * AIChatEffectsCoordinator. Consumers read the slice through the named hook
 * useAIChatConversation() (state + actions) or useAIChatConversationActions() (actions only,
 * via a dedicated context so pure-dispatcher consumers never re-render on state changes). There
 * is no merged value: each slice context is subscribed to directly.
 *
 * CANVAS ISOLATION: the two canvas fields (canvasContent, isCanvasToolExecuting) are carved out of
 * the core Conversation value into their own layered AIChatCanvasContext (read via
 * useAIChatCanvas). In teacher-tools mode setCanvasContent dispatches on EVERY canvas SSE frame;
 * keeping canvas in a separate context means the core value NO LONGER changes identity per frame,
 * so its subscribers skip the churn. The canvas SETTERS stay on the actions object below (their
 * identity is pinned by AIChatConversationProvider.identity.test.js); only the STATE moves.
 *
 * The Conversation-slice lifecycle resets (user-change / AI-just-enabled reset, new-day-on-
 * visibilitychange reset) live in useConversationLifecycleResets, invoked here; they drive this
 * slice through the setters (setCurrentConversationId / setStreamStatus / clearMessageQueue /
 * clearConversationData). Cross-tab storage-sync stays in the coordinator.
 *
 * Action gating: `areActionsEnabled` (AIChatProvider passes isAIEnabled) gates the exposed
 * action set at call time via useGatedActions — state stays readable while writes are suppressed,
 * and the exposed identities never change. The slice itself is generic and knows nothing about
 * AI capability.
 *
 * removePill(id) and removePillsByType(type) wrap their argument into the { id } / { type }
 * payload the reducer expects.
 */
import PropTypes from 'prop-types';
import React, { createContext, useMemo, useReducer } from 'react';
import type { ChatPillConfig } from '../../components/atoms/ChatActionPills/types';
import { type ConversationMode } from '../../constants/modes';
import type { StreamStatus } from '../../constants/status';
import { useGatedActions } from '../useGatedActions';
import { CONVERSATION_ACTIONS, conversationReducer, getInitialConversationState } from './aiChatConversationReducer';
import type { ConversationState } from './aiChatConversationTypes';
import { useConversationLifecycleResets } from './useConversationLifecycleResets';

export type AIChatConversationActions = {
  setChatContext: (context: ConversationState['chatContext']) => void;
  setCurrentConversationId: (id: string | null) => void;
  setConversations: (conversations: unknown[]) => void;
  setStreamStatus: (status: StreamStatus) => void;
  setConversationMode: (mode: ConversationMode) => void;
  queueMessage: (messageData: unknown) => void;
  dequeueMessage: () => void;
  clearMessageQueue: () => void;
  setCanvasContent: (text: string | null | undefined) => void;
  setConversationData: (data: unknown | null) => void;
  clearConversationData: () => void;
  setCanvasToolExecuting: (isExecuting: boolean) => void;
  setIsLoadingConversation: (loading: boolean) => void;
  setLastFailedMessage: (messageData: unknown | null) => void;
  clearLastFailedMessage: () => void;
  addPill: (pill: ChatPillConfig) => void;
  removePill: (id: string) => void;
  removePillsByType: (type: string) => void;
  clearPills: () => void;
};

// Canvas fields are carved OUT of the core value — they change per SSE frame and would otherwise
// churn the whole Conversation value identity. The core value keeps every non-canvas field + the
// full action set (canvas setters included).
export type AIChatConversationValue = Omit<ConversationState, 'canvasContent' | 'isCanvasToolExecuting'> & AIChatConversationActions;

/**
 * Canvas STATE, isolated in its own layered context. Read via useAIChatCanvas(). The canvas
 * setters (setCanvasContent / setCanvasToolExecuting) stay on the Conversation actions object.
 */
export type AIChatCanvasValue = Pick<ConversationState, 'canvasContent' | 'isCanvasToolExecuting'>;

export const AIChatConversationContext = createContext<AIChatConversationValue | null>(null);

/**
 * Actions-only context. Carries the SAME gated action object folded into the merged value, so
 * consumers that only dispatch (never read state) can subscribe here and skip state re-renders.
 */
export const AIChatConversationActionsContext = createContext<AIChatConversationActions | null>(null);

/**
 * Canvas-state-only context. Layered like the actions context so canvas readers (ToolInstanceChatView)
 * re-render on canvas frames while the core Conversation value stays identity-stable. `null` = outside
 * provider.
 */
export const AIChatCanvasContext = createContext<AIChatCanvasValue | null>(null);

type AIChatConversationProviderProps = {
  areActionsEnabled?: boolean;
  children: React.ReactNode;
  initialState?: Partial<ConversationState>;
};

function AIChatConversationProvider({ areActionsEnabled = true, children, initialState = {} }: AIChatConversationProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(conversationReducer, initialState, getInitialConversationState);

  // Setters are memoized once (deps `[]`) — React guarantees `dispatch` is stable for the
  // component's lifetime, so these closures never need to be rebuilt. Keeping them OUT of the
  // `[state]`-keyed value memo is load-bearing: consumers (useConversationEngine → loadConversation /
  // cleanupStreamingResources) fold these setters into their own useCallback deps, and those
  // useCallbacks sit in the dep array of an effect that itself dispatches conversation-state
  // changes. If the setter identities churned on every state change, that effect would re-run and
  // re-dispatch forever ("Maximum update depth exceeded"). See AIChatConversationProvider.identity.test.js.
  const actions = useMemo<AIChatConversationActions>(
    () => ({
      setChatContext: (context: ConversationState['chatContext']) => dispatch({ type: CONVERSATION_ACTIONS.SET_CHAT_CONTEXT, payload: context }),
      setCurrentConversationId: (id: string | null) => dispatch({ type: CONVERSATION_ACTIONS.SET_CURRENT_CONVERSATION_ID, payload: id }),
      setConversations: (conversations: unknown[]) => dispatch({ type: CONVERSATION_ACTIONS.SET_CONVERSATIONS, payload: conversations }),
      setStreamStatus: (status: StreamStatus) => dispatch({ type: CONVERSATION_ACTIONS.SET_STREAM_STATUS, payload: status }),
      setConversationMode: (mode: ConversationMode) => dispatch({ type: CONVERSATION_ACTIONS.SET_CONVERSATION_MODE, payload: mode }),
      queueMessage: (messageData: unknown) => dispatch({ type: CONVERSATION_ACTIONS.QUEUE_MESSAGE, payload: messageData }),
      dequeueMessage: () => dispatch({ type: CONVERSATION_ACTIONS.DEQUEUE_MESSAGE }),
      clearMessageQueue: () => dispatch({ type: CONVERSATION_ACTIONS.CLEAR_MESSAGE_QUEUE }),
      setCanvasContent: (text: string | null | undefined) => dispatch({ type: CONVERSATION_ACTIONS.SET_CANVAS_CONTENT, payload: text }),
      setConversationData: (data: unknown | null) => dispatch({ type: CONVERSATION_ACTIONS.SET_CONVERSATION_DATA, payload: data }),
      clearConversationData: () => dispatch({ type: CONVERSATION_ACTIONS.CLEAR_CONVERSATION_DATA }),
      setCanvasToolExecuting: (isExecuting: boolean) => dispatch({ type: CONVERSATION_ACTIONS.SET_CANVAS_TOOL_EXECUTING, payload: isExecuting }),
      setIsLoadingConversation: (loading: boolean) => dispatch({ type: CONVERSATION_ACTIONS.SET_IS_LOADING_CONVERSATION, payload: loading }),
      setLastFailedMessage: (messageData: unknown | null) => dispatch({ type: CONVERSATION_ACTIONS.SET_LAST_FAILED_MESSAGE, payload: messageData }),
      clearLastFailedMessage: () => dispatch({ type: CONVERSATION_ACTIONS.CLEAR_LAST_FAILED_MESSAGE }),
      addPill: (pill: ChatPillConfig) => dispatch({ type: CONVERSATION_ACTIONS.ADD_PILL, payload: pill }),
      removePill: (id: string) => dispatch({ type: CONVERSATION_ACTIONS.REMOVE_PILL, payload: { id } }),
      removePillsByType: (type: string) => dispatch({ type: CONVERSATION_ACTIONS.REMOVE_PILLS_BY_TYPE, payload: { type } }),
      clearPills: () => dispatch({ type: CONVERSATION_ACTIONS.CLEAR_PILLS }),
    }),
    []
  );

  // Call-time gating keeps the exposed identities permanently stable — never churned by an
  // unrelated state change (see the identity test), and consumers that capture the set once
  // (empty-dep callbacks/effects) keep dispatching correctly after areActionsEnabled flips.
  const exposedActions = useGatedActions(actions, areActionsEnabled);

  // Conversation-slice lifecycle resets, relocated out of the coordinator so they live next to the
  // slice they drive. Invoked UNCONDITIONALLY (its guarding refs must survive isEnabled flips), fed
  // the REAL action set — both effects early-return when disabled, so the ungated setters are never
  // invoked in the disabled state.
  useConversationLifecycleResets({ actions, isEnabled: areActionsEnabled });

  // Canvas fields are pulled out so a per-frame canvas change does NOT churn the core value's
  // identity. The remaining fields are enumerated as the core-value deps; when adding a new
  // reducer field, add it BOTH to the destructure and to this memo's dep list.
  const {
    canvasContent,
    isCanvasToolExecuting,
    chatContext,
    currentConversationId,
    conversations,
    streamStatus,
    mode,
    messageQueue,
    conversationData,
    isLoadingConversation,
    lastFailedMessage,
    pills,
  } = state;

  const value = useMemo<AIChatConversationValue>(
    () => ({
      chatContext,
      currentConversationId,
      conversations,
      streamStatus,
      mode,
      messageQueue,
      conversationData,
      isLoadingConversation,
      lastFailedMessage,
      pills,
      ...exposedActions,
    }),
    [
      chatContext,
      currentConversationId,
      conversations,
      streamStatus,
      mode,
      messageQueue,
      conversationData,
      isLoadingConversation,
      lastFailedMessage,
      pills,
      exposedActions,
    ]
  );

  const canvasValue = useMemo<AIChatCanvasValue>(() => ({ canvasContent, isCanvasToolExecuting }), [canvasContent, isCanvasToolExecuting]);

  return (
    <AIChatConversationActionsContext.Provider value={exposedActions}>
      <AIChatCanvasContext.Provider value={canvasValue}>
        <AIChatConversationContext.Provider value={value}>{children}</AIChatConversationContext.Provider>
      </AIChatCanvasContext.Provider>
    </AIChatConversationActionsContext.Provider>
  );
}

AIChatConversationProvider.propTypes = {
  areActionsEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
};

export default AIChatConversationProvider;
